import { HttpClient } from '../common/httpClient';
import { AxiosHttpClient } from '../common/axiosHttpClient';
import { AccessTokens } from '../../types/auth';
import { Oauth2Principal } from '../../oauth2/oauth2.types';
import { appConfig } from '../../config';

const DEFAULT_OAUTH2_PROVIDER = appConfig.OAUTH2_PROVIDER || 'keycloak';

enum KeycloakAction {
  UPDATE_PASSWORD = 'UPDATE_PASSWORD',
  UPDATE_PROFILE = 'UPDATE_PROFILE'
}

type AccessTokensResponse = {
  readonly token: string;
  readonly chatToken: string;
}

type Oauth2PrincipalResponse = {
  readonly userId: string;
  readonly username: string;
  readonly email: string;
  readonly roles: string[];
  readonly locale?: string;
}

export type LoginRedirectOptions = {
  /**
   * Locale to hint to Keycloak. When provided, both `ui_locales` (OIDC
   * standard) and `kc_locale` (Keycloak vendor param) are appended so
   * that the Keycloak login page renders in the requested language and
   * persists it on the user's profile when supported.
   */
  readonly locale?: string;
}

export class AuthenticationOauth2Api {
  private readonly httpClient: HttpClient;

  constructor() {
    this.httpClient = new AxiosHttpClient({
      baseUrl: appConfig.OAUTH2_BASE_URL,
      withCredentials: true,
    });
  }

  loginRedirect(
    provider: string = DEFAULT_OAUTH2_PROVIDER,
    options: LoginRedirectOptions = {}
  ): void {
    const baseUrl = `${appConfig.OAUTH2_BASE_URL}/oauth2/authorization/${provider}`;
    const query = this.buildLoginRedirectQuery(options);
    window.location.href = query ? `${baseUrl}?${query}` : baseUrl;
  }

  private buildLoginRedirectQuery(options: LoginRedirectOptions): string {
    const params = new URLSearchParams();

    if (options.locale) {
      // `ui_locales` is the OIDC-standard hint; `kc_locale` is Keycloak's
      // vendor-specific param that also persists the selection on the
      // user's profile when the Keycloak locale theme is enabled.
      params.set('ui_locales', options.locale);
      params.set('kc_locale', options.locale);
    }

    return params.toString();
  }

  logout(): Promise<void> {
    return this.httpClient.post<void>(`/logout`, {});
  }

  logoutRedirect(): void {
    window.location.href = `${appConfig.OAUTH2_BASE_URL}/logout`;
  }

  updatePasswordRedirect(): void {
    window.location.href = this.createKeycloakActionUrl(KeycloakAction.UPDATE_PASSWORD);
  }

  private createKeycloakActionUrl(action: KeycloakAction): string {
    const queryParams = Object.entries({
      response_type: 'code',
      client_id: appConfig.KEYCLOAK_CLIENT_ID,
      scope: 'openid',
      redirect_uri: appConfig.KEYCLOAK_ACTION_REDIRECT_URI,
      kc_action: action,
    }).map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
      .join('&');

    return `${appConfig.KEYCLOAK_ISSUER_URI}/protocol/openid-connect/auth?${queryParams}`;
  }

  async getPrincipal(): Promise<Oauth2Principal> {
    const response = await this.httpClient.get<Oauth2PrincipalResponse>('/smsportal/v2/me');

    return {
      userId: response.userId,
      username: response.username,
      email: response.email,
      scopes: response.roles,
      locale: response.locale,
    };
  }

  async exchangeAccessTokens(): Promise<AccessTokens> {
    const response = await this.httpClient.post<AccessTokensResponse>('/smsportal/v2/me/access_tokens', {});

    return {
      token: response.token,
      chatToken: response.chatToken,
    };
  }
}
